import {
  useQueryClient,
  useQuery,
  useMutation,
  keepPreviousData,
} from "@tanstack/react-query";
import qs from "qs";
import { errorMessage, successMessage } from "../../../utils/helper";
import { useNavigate } from "react-router-dom";
import {
  editTipsAndTricks,
  getRecipes,
  getTipsAndTricks,
  getTripsAndTricksList,
  removeTipsAndTricks,
  saveTipsAndProcedureImageVideoUrl,
  saveTipsAndTricks,
} from "../api/TipsAndTricksApi";

const tipsandtricksKeys = {
  all: ["tipsandtricks"],
  lists: () => [...tipsandtricksKeys.all, "ingredientlist"],
  tipsandtrickslist: (filters) => [...tipsandtricksKeys.lists(), filters],
  detail: (id) => [...tipsandtricksKeys.tipsandtrickslist(), id],
};
const recipesKeys = {
  all: ["recipes"],
  lists: () => [...recipesKeys.all, "recipeslist"],
  recipeslist: (filters) => [...recipesKeys.lists(), filters],
  detail: (id) => [...recipesKeys.recipeslist(), id],
};

export const useTipsAndTricksQuery = ({
  tipId,
  debouncedText,
  pagination,
  resetDeleteInfo,
  sorting,
  isList,
  recipeId,
  type,
  filterApplied,
  tipsAndTricks,
}) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  let params = {};
  const categoryId = filterApplied?.map((item) => item?.id);
  if (debouncedText) {
    params = { ...params, search: debouncedText };
  }

  if (categoryId) {
    params = { ...params, category_id: categoryId };
  }
  if (recipeId) {
    params = { ...params, recipe_id: recipeId };
  }
  if (type !== "") {
    params = { ...params, type: type?.value };
  }
  if (sorting?.length > 0) {
    const sortByParams = sorting.reduce(
      (obj, item) =>
        Object.assign(obj, { [item.id]: item.desc ? "desc" : "asc" }),
      {}
    );
    params = {
      ...params,
      sort: sortByParams,
    };
  }
  const query = {
    params,
    paramsSerializer: (params) => qs.stringify(params, { encode: true }),
  };

  const listQuery = useQuery({
    queryKey: tipsandtricksKeys.tipsandtrickslist({ query, pagination }),
    queryFn: () => getTripsAndTricksList({ query, pagination }),
    placeholderData: keepPreviousData,
    enabled: isList,
  });
  const recipeListQuery = useQuery({
    queryKey: recipesKeys.recipeslist({ query, pagination }),
    queryFn: () => getRecipes({ query, pagination }),
    placeholderData: keepPreviousData,
    enabled: true,
  });

  const createTipsAndTricks = useMutation({
    mutationFn: ({ formData }) => saveTipsAndTricks(formData),
    onError: (error) => {
      errorMessage(error.response.data);
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: tipsandtricksKeys.lists(),
      });
      tipsAndTricks.data.type === "Tips & Tricks"
        ? successMessage("Tips successfully created")
        : successMessage("Procedure successfully created");
      navigate(`/tips-tricks`);
    },
  });
  const deleteTipsAndTricks = useMutation({
    mutationFn: removeTipsAndTricks,
    onError: (error) => {
      errorMessage(error?.response?.data);
    },
    onSuccess: (res, variables) => {
      if (res.success) {
        if (variables.isList) {
          queryClient.invalidateQueries({
            queryKey: tipsandtricksKeys.lists(),
          });
        }
        successMessage(res.message);
        variables.to && navigate(variables.to);
        resetDeleteInfo();
      } else if (res.success === false) {
        errorMessage(res.message);
      }
    },
  });
  const tipsAndTricksDetails = useQuery({
    queryKey: tipsandtricksKeys.detail(tipId),
    queryFn: () => getTipsAndTricks(tipId),
    enabled: tipId > 0,
  });
  const updateTipsAndTricks = useMutation({
    mutationFn: ({ formData }) => editTipsAndTricks(tipId, formData),
    onError: (error) => {
      errorMessage(error.response.data);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: tipsandtricksKeys.lists(),
      });
      tipsAndTricks.data.type === "Tips & Tricks"
        ? successMessage("Tips successfully updated")
        : successMessage("Procedure successfully updated");
      navigate(`/tips-tricks`);
    },
  });
  const createImageFileUrl = useMutation({
    mutationFn: saveTipsAndProcedureImageVideoUrl,
    onError: (error) => {
      errorMessage(error.response.data);
    },
  });

  return {
    listQuery,
    recipeListQuery,
    deleteTipsAndTricks,
    createTipsAndTricks,
    tipsAndTricksDetails,
    updateTipsAndTricks,
    createImageFileUrl,
  };
};
